import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import { Box, Button, DialogActions, DialogContent, Grid } from "@mui/material";
import Alert from "@mui/material/Alert";
import {
  SaveEmailThemeRequest,
  SettingCache,
} from "services/KnowfullyAdminClient";
import Field from "components/Field";
import useGlobalContext from "hooks/globalData";
import { FORM_ERROR } from "final-form";
import translateError from "utils/translateError";
import ContextOrganizationField, {
  ContextOrganizationFieldMode,
} from "components/ContextOrganizationField";
import FieldRichText from "components/FieldRichText";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";

const styles = {
  feedback: {
    width: "100%",
    margin: "15px 0 0 15px",
  },
};

const { Form } = withTypes<SaveEmailThemeRequest>();

type EmailThemeFormProps = {
  organizationId: string;
  emailTheme?: SettingCache;
  submitLabel: string;
};

const EmailThemeForm = ({
  organizationId,
  emailTheme,
  submitLabel,
}: EmailThemeFormProps) => {
  const { formatMessage: t } = useIntl();
  const { emailThemeUrl, notificationService } = useGlobalContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const init = {
    organizationId,
    value: emailTheme?.value,
  };
  const onSubmit = async (values: SaveEmailThemeRequest) => {
    try {
      const response = await notificationService.saveEmailTheme(
        new SaveEmailThemeRequest(values)
      );
      queryClient.invalidateQueries("settingById");
      queryClient.invalidateQueries("settings");
      queryClient.invalidateQueries("getEmailTheme");

      navigate(emailThemeUrl());

      return response;
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      render={({ handleSubmit, submitting, submitError, values, form }) => (
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <ContextOrganizationField
                values={values}
                formChange={form.change}
                mode={ContextOrganizationFieldMode.EditDisabled}
              />
              <Grid item xs={12}>
                <Field name="value" required>
                  {({ input, meta }) => {
                    return (
                      <FieldRichText
                        input={input}
                        meta={meta}
                        init={{
                          height: 500,
                        }}
                      />
                    );
                  }}
                </Field>
              </Grid>

              {submitError && (
                <Box sx={styles.feedback}>
                  <Alert severity="error">{submitError}</Alert>
                </Box>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button
              disabled={submitting}
              onClick={() => navigate(emailThemeUrl())}
            >
              {t({ id: "cancelButton" })}
            </Button>

            <Button
              type="submit"
              color="primary"
              disabled={submitting || !values.value}
            >
              {submitLabel}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
};

export default EmailThemeForm;
